import { createContext, useState, useEffect } from "react";
import { flushSync } from "react-dom";
import jwtDecode from "jwt-decode";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export default AuthContext;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// Called on login — registers the SW and silently syncs any existing push subscription
async function registerServiceWorker(backendUrl, accessToken) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    await navigator.serviceWorker.register('/sw.js');
    // If this browser already has a push subscription (e.g. from a previous session),
    // make sure it's registered in the backend — no prompt needed.
    if (Notification.permission === 'granted') {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        fetch(backendUrl + 'api/web-push/subscribe/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken },
          body: JSON.stringify(existing.toJSON()),
        }).catch(() => {});
      }
    }
  } catch (_) {}
}

// Called from a user-gesture (button tap) — requests permission and subscribes
async function subscribeToPush(backendUrl, accessToken) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'unsupported';
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  try {
    const reg = await navigator.serviceWorker.ready;

    const keyRes = await fetch(backendUrl + 'api/web-push/vapid-public-key/', {
      headers: { Authorization: 'Bearer ' + accessToken },
    });
    if (!keyRes.ok) return 'error';
    const { vapid_public_key } = await keyRes.json();

    // Always force a fresh subscription so the stored endpoint is always
    // signed with the current VAPID key. Stale subscriptions (e.g. after a
    // key rotation) silently fail at FCM even though the send returns 200.
    const existing = await reg.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid_public_key),
    });

    // Always register with backend — idempotent (update_or_create on endpoint)
    const res = await fetch(backendUrl + 'api/web-push/subscribe/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken },
      body: JSON.stringify(subscription.toJSON()),
    });
    return res.ok ? 'subscribed' : 'error';
  } catch (_) {
    return 'error';
  }
}

// Unsubscribe from web push and remove from backend
async function unsubscribeFromPush(backendUrl, accessToken) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await fetch(backendUrl + 'api/web-push/unsubscribe/', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + accessToken },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
      await sub.unsubscribe();
    }
    return 'unsubscribed';
  } catch (_) {
    return 'error';
  }
}

// Check current push permission/subscription state (no prompt)
async function getPushStatus() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) return 'subscribed';
    return Notification.permission === 'granted' ? 'not_subscribed' : 'not_asked';
  } catch (_) { return 'unsupported'; }
}

export const AuthProvider = ({ children }) => {
  // Admin impersonation: ?impersonate=<base64-json> sets tokens before state init
  try {
    const params = new URLSearchParams(window.location.search);
    const imp = params.get('impersonate');
    if (imp) {
      const tokens = JSON.parse(atob(imp));
      if (tokens?.access && tokens?.refresh) {
        localStorage.setItem('authTokens', JSON.stringify(tokens));
        params.delete('impersonate');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
      }
    }
  } catch (_) {}

  let tempToken = null;
  try {
    tempToken = JSON.parse(localStorage.getItem("authTokens"));
  } catch (err) {
    tempToken = null;
  }
  try {
    if (
      tempToken.detail === "No active account found with the given credentials"
    ) {
      localStorage.removeItem("authTokens");
      tempToken = null;
    }
  } catch (err) {}

  const [profile, setProfile] = useState(null);
  const [schoolname, setSchoolname] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "") === ""
    ? (process.env.NODE_ENV === "production" ? "/" : "http://127.0.0.1:8000/")
    : process.env.REACT_APP_BACKEND_URL;
  const pageTitle = 'SB2';

  const getProfile = async () => {
    let response = await fetch(backendUrl + "api/profile", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + String(authTokens.access),
      },
    });
    let data = await response.json();
    if (response.status === 200) {
      setProfile(data);
      setSchoolname(data["schoolname"]);
      navigate("/");
    } else if (response.statusText === "Unauthorized") {
      logoutUser();
    }
  };

  const getUserProfile = async (userAccess) => {
    let response = await fetch(backendUrl + "api/profile", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + String(userAccess),
      },
    });
    let data = await response.json();
    if (response.status === 200) {
      setProfile(data);
      setSchoolname(data["schoolname"]);
      navigate("/");
    } else if (response.statusText === "Unauthorized") {
      logoutUser();
    }
  };

  let [user, setUser] = useState(() => {
    if (tempToken !== null) {
      try {
        return jwtDecode(localStorage.getItem("authTokens"));
      } catch (error) {
        return null;
      }
    }
    return null;
  });

  let [authTokens, setAuthTokens] = useState(() =>
    localStorage.getItem("authTokens")
      ? JSON.parse(localStorage.getItem("authTokens"))
      : null
  );
  let [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  let loginUser = async (e) => {
    e.preventDefault();
    flushSync(() => setLoginLoading(true));
    try {
      const response = await fetch(backendUrl + "api/token/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: e.target.email.value,
          password: e.target.password.value,
        }),
      });

      if (response.status === 429) {
        setLoginError("too_many_requests");
        setLoginLoading(false);
        return;
      }
      let data = await response.json();
      if (response.status === 200 && data.access) {
        setLoginError(null);
        localStorage.setItem("authTokens", JSON.stringify(data));
        setAuthTokens(data);
        const decoded = jwtDecode(data.access);
        setUser(decoded);
        const role = decoded?.profile_type ?? 0;
        registerServiceWorker(backendUrl, data.access);
        if (role === 0 || role === 3) {
          // Admin/teacher: fetch profile (which also sets schoolname) then navigate
          await getUserProfile(data.access);
        } else {
          // Driver or parent: navigate directly, skip admin profile endpoint
          navigate(role === 2 ? "/driver/map" : "/parent/today");
        }
        setLoginLoading(false);
      } else {
        setLoginError(data.error_code || "unknown");
        setLoginLoading(false);
      }
    } catch (error) {
      setLoginError("connection_error");
      setLoginLoading(false);
    }
  };

  let logoutUser = (e) => {
    localStorage.removeItem("authTokens");
    setAuthTokens(null);
    setUser(null);
    navigate("/login");
  };

  const updateToken = async () => {
    if (!authTokens?.refresh) {
      if (loading) setLoading(false);
      return null;
    }
    try {
      const response = await fetch(backendUrl + "api/token/refresh/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: authTokens.refresh }),
      });
      const data = await response.json();
      if (response.status === 200) {
        setAuthTokens(data);
        setUser(jwtDecode(data.access));
        localStorage.setItem("authTokens", JSON.stringify(data));
        return data.access;
      } else {
        logoutUser();
        return null;
      }
    } catch (error) {
      logoutUser();
      return null;
    } finally {
      if (loading) setLoading(false);
    }
  };

  const userRole = user?.profile_type ?? 0;

  let contextData = {
    user: user,
    authTokens: authTokens,
    loginUser: loginUser,
    logoutUser: logoutUser,
    backendUrl: backendUrl,
    profile: profile,
    schoolname: schoolname,
    pageTitle: pageTitle,
    loginError: loginError,
    loginLoading: loginLoading,
    userRole: userRole,
    loading: loading,
    refreshAccessToken: updateToken,
    subscribeToPush: () => subscribeToPush(backendUrl, authTokens?.access),
    unsubscribeFromPush: () => unsubscribeFromPush(backendUrl, authTokens?.access),
    getPushStatus,
  };

  useEffect(() => {
    if (authTokens) {
      // Refresh the token first, then fetch profile / sync push subscription with the
      // guaranteed-fresh access token. This prevents silent failures (401s) when the
      // stored access token has already expired (access tokens live 60 minutes, so
      // this is the common case for a returning user reopening a bookmarked tab).
      updateToken().then((newAccess) => {
        if (!newAccess) return; // logged out
        const role = user?.profile_type ?? 0;
        if (role === 0 || role === 3) {
          fetch(backendUrl + "api/profile", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + newAccess,
            },
          })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
              if (data) {
                setProfile(data);
                setSchoolname(data["schoolname"]);
              }
            })
            .catch(() => {});
        }
        // Silently sync push subscription on page load (covers already-logged-in users)
        registerServiceWorker(backendUrl, newAccess);
      });
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const REFRESH_INTERVAL = 1000 * 10 * 10;
    let interval = setInterval(() => {
      if (authTokens) {
        updateToken();
      }
    }, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [authTokens]);

  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};
